import type {
  ICatalogTemplate,
  ICatalogPost,
  IDailyCatalogPost,
  ICategoryPosition,
  IPostInCategoryPosition,
  ICursorPaginatedResponse,
  ICreatePostFromFilePayload,
  ICreatePostFromFileResponse,
  IGeneratePostUsingMlResponse,
  IGetMlResultResponse,
  IListDailyPostsParams,
  ILokaliseKey,
} from '~/types/catalog'

import config from '~/config'
import { paramsSerializer } from '~/utils/api'
import { request } from '~/utils/request'

/*******************
 * Catalog
 *******************/

// Categories

export const getCategories = (): Promise<ICategoryPosition[]> => {
  return request({
    url: `${config.PROCOLOR_API}/catalog/categories`,
  })
}

//Templates

export const getTemplateById = (templateId: string): Promise<ICatalogTemplate> => {
  return request({
    url: `${config.PROCOLOR_API}/templates/search-by-id/${templateId}`,
  })
}

export const searchTemplates = (
  query?: string,
  nextPageCursor?: string
): Promise<ICursorPaginatedResponse<ICatalogTemplate>> => {
  return request({
    params: {
      page_cursor: nextPageCursor,
      q: query,
      size: 30,
    },
    url: `${config.PROCOLOR_API}/templates/search`,
  })
}

export const recentTemplate = (
  originType?: string,
  nextPageCursor?: string
): Promise<ICursorPaginatedResponse<ICatalogTemplate>> => {
  return request({
    params: {
      origin_type: originType,
      page_cursor: nextPageCursor,
      size: 30,
    },
    url: `${config.PROCOLOR_API}/templates/recent`,
  })
}

export const popularTemplate = (originType?: string): Promise<ICatalogTemplate> => {
  return request({
    params: {
      origin_type: originType,
    },
    url: `${config.PROCOLOR_API}/templates/popular`,
  })
}

export const mostLikedTemplate = (originType?: string): Promise<ICatalogTemplate> => {
  return request({
    params: {
      origin_type: originType,
    },
    url: `${config.PROCOLOR_API}/templates/most-liked`,
  })
}

export const addPostToCategory = async (data: {
  category_name?: string
  enabled: boolean
  post_id: string
}): Promise<void> => {
  return request({
    data,
    method: 'POST',
    url: `${config.PROCOLOR_API}/catalog/posts/create`,
  })
}

export const createDailyPost = async (data: {
  coloring_template_id: string
  day: string
}): Promise<ICatalogTemplate> => {
  return request({
    data,
    method: 'POST',
    url: `${config.PROCOLOR_API}/daily-coloring-template`,
  })
}

export const createCategory = async (data: {
  category_name: string
  enabled: boolean
  lokalise_key: string
}): Promise<void> => {
  return request({
    data,
    method: 'POST',
    url: `${config.PROCOLOR_API}/catalog/categories/create`,
  })
}

export const updateCategory = async (data: { category_name: string; lokalise_key: string }): Promise<void> => {
  return request({
    data,
    method: 'PUT',
    url: `${config.PROCOLOR_API}/catalog/categories`,
  })
}

export const deleteCategory = async (data: { category_name: string }): Promise<void> => {
  return request({
    data,
    method: 'POST',
    url: `${config.PROCOLOR_API}/catalog/categories/delete`,
  })
}

export const updateCategoriesPositions = async ({ positions }: { positions: ICategoryPosition[] }): Promise<void> => {
  return request({
    data: positions,
    method: 'PUT',
    url: `${config.PROCOLOR_API}/catalog/categories/positions`,
  })
}

export const enableCategory = async (data: { category_name: string; enabled: boolean }): Promise<void> => {
  return request({
    data,
    method: 'POST',
    url: `${config.PROCOLOR_API}/catalog/categories/enable`,
  })
}

export const getLokaliseKeys = (): Promise<ILokaliseKey[]> => {
  return request({
    url: `${config.PROCOLOR_API}/catalog/lokalise-keys`,
  })
}
//Posts

export const listCategoryPosts = (categoryName: string): Promise<ICatalogPost[]> => {
  return request({
    data: { category_name: categoryName },
    method: 'POST',
    url: `${config.PROCOLOR_API}/catalog/posts`,
  })
}

export const deletePostFromCategory = async (data: { categoryName: string; postId: string }): Promise<void> => {
  return request({
    data: { category_name: data.categoryName },
    method: 'POST',
    url: `${config.PROCOLOR_API}/catalog/posts/${data.postId}/delete`,
  })
}

export const enablePostInCategory = async (data: {
  categoryName: string
  enabled: boolean
  postId: string
}): Promise<void> => {
  return request({
    data: { category_name: data.categoryName, enabled: data.enabled },
    method: 'PUT',
    url: `${config.PROCOLOR_API}/catalog/posts/${data.postId}`,
  })
}

export const updatePostsInCategoryPositions = async ({
  positions,
}: {
  positions: IPostInCategoryPosition[]
}): Promise<void> => {
  return request({
    data: positions,
    method: 'PUT',
    url: `${config.PROCOLOR_API}/catalog/posts/positions`,
  })
}

/** @doc https://dev.admin.procolor-backend.mwmwebapis.com/#post-/coloring-template */
export const createPostFromFile = async (data: ICreatePostFromFilePayload): Promise<ICreatePostFromFileResponse> => {
  return request({
    data,
    method: 'POST',
    url: `${config.PROCOLOR_API}/coloring-template`,
  })
}

/** @doc https://dev.admin.procolor-backend.mwmwebapis.com/#post-/coloring-template/ml/generate */
export const generatePostUsingMl = async (data: { prompt: string }): Promise<IGeneratePostUsingMlResponse> => {
  return request({
    data,
    method: 'POST',
    url: `${config.PROCOLOR_API}/coloring-template/ml/generate`,
  })
}

/** @doc https://dev.admin.procolor-backend.mwmwebapis.com/#get-/coloring-template/ml/request/-requestID-  */
export const getMlResult = async (requestId: string): Promise<IGetMlResultResponse> => {
  return request({
    url: `${config.PROCOLOR_API}/coloring-template/ml/request/${requestId}`,
  })
}

const dateParamsKeyName = (key: string) => {
  switch (key) {
    case 'startDate':
      return 'start_date'
    case 'endDate':
      return 'end_date'
    default:
      return key
  }
}

/** @doc https://dev.admin.procolor-backend.mwmwebapis.com/#get-/daily/posts */
export const listDailyPosts = async (params: IListDailyPostsParams): Promise<IDailyCatalogPost[]> => {
  const queryParams = paramsSerializer(params, dateParamsKeyName)

  return request({
    url: `${config.PROCOLOR_API}/daily/posts?${queryParams}`,
  })
}

/** @doc https://dev.admin.procolor-backend.mwmwebapis.com/#delete-/daily-coloring-template/-day- */
export const deleteDailyPost = async (day: string): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.PROCOLOR_API}/daily-coloring-template/${day}`,
  })
}
